import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABEL } from "../lib/auth";
import { useDrafts, useOnline } from "../lib/useOffline";
import {
  LayoutDashboard,
  PlusCircle,
  Database,
  Map as MapIcon,
  FileText,
  Sparkles,
  Settings,
  LogOut,
  Sprout,
  CloudOff,
  Wifi,
  WifiOff,
} from "lucide-react";
import { Button } from "./ui/button";
import { Toaster } from "./ui/sonner";
import Brand from "./Brand";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/collections/new", label: "New Collection", icon: PlusCircle, testid: "nav-new-collection" },
  { to: "/collections", label: "Collections", icon: Database, testid: "nav-collections" },
  { to: "/map", label: "Map", icon: MapIcon, testid: "nav-map" },
  { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
  { to: "/provenance", label: "Provenance Intelligence", icon: Sparkles, testid: "nav-provenance" },
  { to: "/drafts", label: "Offline Drafts", icon: CloudOff, testid: "nav-drafts" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();
  const { pending } = useDrafts();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout">
      <aside className="w-72 sk-sidebar-bg border-r border-[#E6E2D8] flex flex-col sticky top-0 h-screen" data-testid="sidebar">
        <div className="p-7 border-b border-[#E6E2D8]">
          <Brand variant="inline" tone="default" size={44} testid="sidebar-brand" />
          <p className="mt-5 text-[12px] leading-relaxed text-[#5C5854] font-serif-display italic">
            Seed provenance intelligence for restoration, culture and climate resilience.
          </p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-[13.5px] transition-colors ${
                  isActive
                    ? "bg-[#8B5A2B] text-[#FDFBF7]"
                    : "text-[#2C2A28] hover:bg-[#E6E2D8]/70"
                }`
              }
            >
              <item.icon strokeWidth={1.6} className="w-[18px] h-[18px]" />
              <span className="flex-1">{item.label}</span>
              {item.to === "/drafts" && pending > 0 && (
                <span
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#D08C38] text-[#FDFBF7] text-[10px] font-semibold"
                  data-testid="nav-drafts-badge"
                >
                  {pending}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 pb-3">
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[11px] font-semibold ${
              online
                ? "bg-[#EEF2EE] border-[#B5C2B8] text-[#36453A]"
                : "bg-[#FFF7E8] border-[#D08C38] text-[#9B6716]"
            }`}
            data-testid="sidebar-network-status"
          >
            {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            <span className="tracking-[0.18em] uppercase">{online ? "Online" : "Field mode"}</span>
            {!online && pending > 0 && (
              <span className="ml-auto text-[10px]">{pending} draft{pending === 1 ? "" : "s"}</span>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-[#E6E2D8]">
          {user && user !== false && (
            <div className="mb-3 p-3 rounded-md bg-white border border-[#E6E2D8]">
              <div className="text-[13px] font-semibold text-[#2C2A28]" data-testid="sidebar-user-name">{user.name}</div>
              <div className="text-[11px] text-[#8A857D] truncate" data-testid="sidebar-user-email">{user.email}</div>
              <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] mt-1" data-testid="sidebar-user-role">
                {ROLE_LABEL[user.role] || user.role}
              </div>
              {user.organisation && (
                <div className="text-[11px] text-[#5C5854] mt-1">{user.organisation}</div>
              )}
            </div>
          )}
          <Button
            variant="outline"
            className="w-full justify-start gap-2 border-[#E6E2D8] text-[#2C2A28] hover:bg-[#E6E2D8]/70"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.6} /> Sign out
          </Button>
          <a
            href="/atlas"
            target="_blank"
            rel="noreferrer"
            className="block mt-3 text-[10.5px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] hover:underline underline-offset-4 text-center"
            data-testid="sidebar-atlas-link"
          >
            View the public Restoration Atlas ↗
          </a>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="px-8 lg:px-12 py-10 max-w-[1400px]">
          <Outlet />
        </div>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
